import { AfterViewInit, Component, effect, inject, signal, TemplateRef, viewChild } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { createRefreshScheduler } from "@kno2/core/browser";
import { LocalStorageService } from "@kno2/core/storage";
import { Message, MessagesQuery, MessagesService } from "@kno2/data-access/messages";
import { ConfirmationDialogService } from "@kno2/shared/confirmation-dialog";
import { PaginationComponent } from "@kno2/shared/pagination";
import { TableColumn, TableComponent } from "@kno2/shared/table";
import { addMinutes, format, secondsToMilliseconds } from "date-fns";

const SIGNATURES_DRAFTS_PAGE_KEY = "getSignatures.draftsPage";

@Component({
    selector: "kno2-signatures-drafts",
    imports: [TableComponent, PaginationComponent, RouterLink],
    templateUrl: "./signatures-drafts.component.html"
})
export class SignaturesDraftsComponent implements AfterViewInit {
    private readonly router = inject(Router);
    private readonly messagesService = inject(MessagesService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly refreshScheduler = createRefreshScheduler(secondsToMilliseconds(15), () => this.pageOptions.update((options) => ({ ...options })));

    protected subjectTemplate = viewChild<TemplateRef<{ message: unknown }>>("subject");
    protected toAddressTemplate = viewChild<TemplateRef<{ message: unknown }>>("toAddress");

    protected pageOptions = signal<MessagesQuery>({
        pageStart: Number(this.localStorageService.get(SIGNATURES_DRAFTS_PAGE_KEY)) || 1,
        pageSize: 10,
        isDraft: true
    });
    protected setPageStartEffect = effect(() => this.localStorageService.set(SIGNATURES_DRAFTS_PAGE_KEY, this.pageOptions().pageStart));

    protected messages = this.messagesService.getMessages(this.pageOptions);
    protected columns = signal<TableColumn<Message>[]>([]);

    public ngAfterViewInit(): void {
        this.columns.set(this.getColumns());
    }

    public updateMessagesQuery(query?: MessagesQuery): void {
        this.pageOptions.update((options) => ({
            ...options,
            ...(query || {})
        }));
        this.refreshScheduler.reset();
    }

    public editDraft(id: string): void {
        this.router.navigate(["signatures", "draft", id]);
    }

    public async deleteDraft(id: string): Promise<void> {
        await this.confirmationDialogService.open({
            windowClass: "modal-600",
            headerText: "Delete Draft",
            bodyText: `
              <p>You are about to delete this draft.</p>
              <p>Are you sure?</p>`,
            confirmFn: async () => {
                await this.messagesService.deleteMessage(id);
            },
            successNotificationText: "The draft was deleted successfully."
        });
        this.updateMessagesQuery();
    }

    private getColumns(): TableColumn<Message>[] {
        return [
            {
                field: "createdDate",
                headerText: "Date",
                formatter: ({ createdDate }) => format(addMinutes(new Date(createdDate), -new Date().getTimezoneOffset()), "MMM d, y")
            },
            {
                field: "toAddress",
                headerText: "To",
                cellClass: "overflow-visible k2-ellipsis",
                width: "25%",
                templateRef: this.toAddressTemplate()
            },
            {
                field: "subject",
                templateRef: this.subjectTemplate()
            }
        ];
    }
}
